import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, DoorOpen, Fence, Warehouse, Flame, TrendingUp, Ruler, Shield, Phone, MapPin, MessageCircle, ArrowRight, HardHat, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const LOGO = "/logo.webp";
// WhatsApp de la empresa (instancia "grproducciones" en Evolution) — lo que entra cae en Charlas
const WA_LINK = `https://wa.me/5491140910945?text=${encodeURIComponent("Hola GR Soluciones Integrales, quiero hacer una consulta.")}`;

const SERVICES = [
  { icon: Building2, title: "Estructuras para Edificios", desc: "Estructuras metálicas para edificios en altura, entrepisos, refuerzos estructurales y obra nueva. Cálculo, fabricación y montaje.", img: "https://images.pexels.com/photos/3818947/pexels-photo-3818947.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", big: true },
  { icon: HardHat, title: "Grandes Obras", desc: "Galpones industriales, tinglados, naves y obras civiles de gran escala. Trabajamos con constructoras, estudios y desarrolladores." },
  { icon: DoorOpen, title: "Portones Industriales y Residenciales", desc: "Portones corredizos, levadizos, batientes y con automatización. Para consorcios, industrias y viviendas." },
  { icon: Shield, title: "Rejas y Seguridad Perimetral", desc: "Rejas de seguridad, cercos perimetrales y protecciones para obras, comercios y hogares." },
  { icon: Warehouse, title: "Techos y Cubiertas Metálicas", desc: "Techos de chapa, cubiertas autoportantes, estructuras de techo y desagües para todo tipo de superficie." },
  { icon: Flame, title: "Frentes de Parrilla y Quinchos", desc: "Frentes de parrilla a medida, campanas, quinchos y accesorios de fundición para exteriores." },
  { icon: Ruler, title: "Escaleras y Barandas", desc: "Escaleras rectas, caracol, de emergencia y barandas de seguridad normalizadas." },
  { icon: Fence, title: "Cerramientos y Frentes", desc: "Cerramientos de obra, frentes integrales, pérgolas y carpintería metálica a medida." },
];

const SERVICE_OPTIONS = ["Estructuras para edificios", "Grandes obras", "Portones", "Rejas y seguridad perimetral", "Techos y cubiertas", "Frentes de parrilla y quinchos", "Escaleras y barandas", "Cerramientos", "Otro"];

const STATS = [
  ["+15", "Años de oficio"],
  ["+200", "Obras entregadas"],
  ["24 hs", "Respuesta a consultas"],
  ["100%", "Fabricación propia"],
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_type: "Otro", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Completá nombre, teléfono y mensaje");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", form);
      toast.success(`Consulta enviada. Seguimiento: ${data.tracking_code || data.id}`);
      setForm({ name: "", phone: "", email: "", service_type: "Otro", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white text-neutral-900 min-h-screen relative">
      {/* Top bar */}
      <div className="bg-[#1C1C1C] text-neutral-300 text-xs py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <span className="tracking-wider uppercase font-medium">Herrería industrial y residencial · Grandes obras</span>
          <span className="hidden sm:flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-[#F2B90D]" /> Consultas por WhatsApp y formulario — respondemos en el día</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="sticky top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="#inicio" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO} alt="GR Soluciones Integrales" className="h-14 w-auto" />
            <div className="hidden sm:block leading-tight">
              <p className="font-heading font-extrabold uppercase tracking-tight text-lg text-neutral-900">GR Soluciones Integrales</p>
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">Estructuras · Portones · Grandes obras</p>
            </div>
          </a>
          <div className="flex items-center gap-6">
            <a href="#servicios" className="hidden md:block text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-testid="nav-servicios">Servicios</a>
            <a href="#proceso" className="hidden md:block text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-testid="nav-proceso">Cómo trabajamos</a>
            <a href="#contacto" className="hidden lg:block">
              <Button className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase text-xs tracking-wider" data-testid="nav-cta-btn">
                Pedir presupuesto
              </Button>
            </a>
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-neutral-300 text-neutral-700 rounded-sm font-heading uppercase text-xs tracking-wider hover:bg-neutral-100 hover:text-neutral-900" data-testid="nav-login-btn">
                Ingresar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section id="inicio" className="relative bg-[#141414] text-white">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: "url(https://images.pexels.com/photos/3818947/pexels-photo-3818947.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#F2B90D] text-black text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 mb-6">
              <HardHat className="w-3.5 h-3.5" /> Fabricación y montaje propio
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold uppercase tracking-tighter leading-[0.95]">
              Estructuras metálicas<br />para obras de <span className="text-[#F2B90D]">gran escala</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-2xl leading-relaxed">
              Edificios, galpones, portones, rejas, techos y frentes de parrilla. Del plano a la obra terminada: relevamiento, cotización formal, fabricación en taller y montaje en obra.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#contacto">
                <Button className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase tracking-wider text-sm h-13 px-8 py-6" data-testid="hero-cta-btn">
                  Solicitar cotización <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#servicios">
                <Button variant="outline" className="border-neutral-500 text-white bg-transparent rounded-sm font-heading uppercase tracking-wider text-sm px-8 py-6 hover:bg-white/10 hover:text-white" data-testid="hero-servicios-btn">
                  Ver servicios
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
        {/* Stats strip */}
        <div className="relative border-t border-white/10 bg-black/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map(([v, l]) => (
              <div key={l} className="flex items-baseline gap-3">
                <span className="text-3xl font-heading font-extrabold text-[#F2B90D]">{v}</span>
                <span className="text-xs uppercase tracking-wider text-neutral-400">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-24 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#B8890A] mb-3">Servicios</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold uppercase tracking-tight text-neutral-900">De la reja a medida<br />a la obra completa</h2>
          </div>
          <p className="text-neutral-500 max-w-md text-sm leading-relaxed">Atendemos particulares, consorcios, arquitectos y constructoras con el mismo estándar: materiales certificados, terminaciones prolijas y plazos cumplidos.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.07 }}
              className={`group bg-white border border-neutral-200 rounded-md overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-200 ${s.big ? "md:col-span-2" : ""}`} data-testid={`service-card-${i}`}>
              {s.img && (
                <div className="h-44 bg-cover bg-center relative" style={{ backgroundImage: `url(${s.img})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              )}
              <div className="p-6">
                <div className="w-11 h-11 bg-[#F2B90D]/15 border border-[#F2B90D]/40 rounded-sm flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#B8890A]" />
                </div>
                <h3 className="font-heading font-bold uppercase text-base mb-2 text-neutral-900">{s.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Proceso */}
      <section id="proceso" className="py-24 bg-[#1C1C1C] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#F2B90D] mb-3">Cómo trabajamos</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold uppercase tracking-tight mb-14">Un proceso claro,<br />de la consulta a la entrega</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              ["01", "Consulta", "Nos contás tu proyecto por el formulario o WhatsApp: medidas, planos o fotos de referencia."],
              ["02", "Relevamiento y cotización", "Visitamos la obra si hace falta y te enviamos un presupuesto formal detallado, directo a tu WhatsApp."],
              ["03", "Fabricación en taller", "Cortes optimizados, soldadura y terminación con control de calidad en cada etapa."],
              ["04", "Montaje y entrega", "Instalación en obra con equipo propio, limpieza final y garantía sobre el trabajo."],
            ].map(([n, t, d]) => (
              <div key={n} className="border-t-2 border-[#F2B90D] pt-6">
                <span className="font-mono text-[#F2B90D] text-sm font-bold">{n}</span>
                <h3 className="font-heading font-bold uppercase text-lg mt-3 mb-3">{t}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quién */}
      <section className="py-20 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#B8890A] mb-3">Con quién trabajamos</p>
            <h2 className="text-2xl sm:text-3xl font-heading font-extrabold uppercase tracking-tight text-neutral-900 mb-6">Del particular a la constructora</h2>
            <div className="space-y-4">
              {[
                "Constructoras y desarrolladores: estructuras, grandes obras y provisión continua",
                "Arquitectos y estudios: herrería de diseño y detalles a medida para sus proyectos",
                "Consorcios y comercios: portones, rejas, cerramientos y mantenimiento",
                "Particulares: frentes de parrilla, techos, escaleras y trabajos para el hogar",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#B8890A] shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="h-80 rounded-md bg-cover bg-center shadow-xl" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000)" }} />
            <div className="absolute -bottom-6 -left-6 bg-[#F2B90D] text-black px-6 py-4 rounded-sm shadow-lg hidden sm:block">
              <p className="font-heading font-extrabold uppercase text-sm">Presupuesto sin cargo</p>
              <p className="text-xs font-medium">Respuesta en el día</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section id="contacto" className="py-24 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#B8890A] mb-3">Contacto</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold uppercase tracking-tight text-neutral-900 mb-6">Contanos tu proyecto</h2>
          <p className="text-neutral-500 leading-relaxed mb-10">Ya sea una reja, un portón industrial o la estructura completa de un edificio: dejanos tu consulta y te enviamos una cotización formal a medida.</p>
          <div className="space-y-5 text-sm text-neutral-700">
            <div className="flex items-center gap-3"><div className="w-9 h-9 bg-[#F2B90D]/15 rounded-sm flex items-center justify-center"><Phone className="w-4 h-4 text-[#B8890A]" /></div> Respondemos todas las consultas en el día</div>
            <div className="flex items-center gap-3"><div className="w-9 h-9 bg-[#F2B90D]/15 rounded-sm flex items-center justify-center"><MapPin className="w-4 h-4 text-[#B8890A]" /></div> Relevamiento en obra y fabricación en taller propio</div>
            <div className="flex items-center gap-3"><div className="w-9 h-9 bg-[#25D366]/15 rounded-sm flex items-center justify-center"><MessageCircle className="w-4 h-4 text-[#1faa52]" /></div> Cotización y seguimiento de tu obra por WhatsApp</div>
          </div>
        </div>
        <form onSubmit={submit} className="bg-[#1C1C1C] rounded-md p-8 space-y-4 shadow-2xl" data-testid="contact-form">
          <h3 className="font-heading font-bold uppercase text-white text-lg mb-2">Solicitá tu presupuesto</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input placeholder="Nombre *" value={form.name} onChange={set("name")} className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 rounded-sm h-11" data-testid="contact-name-input" />
            <Input placeholder="Teléfono / WhatsApp *" value={form.phone} onChange={set("phone")} className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 rounded-sm h-11" data-testid="contact-phone-input" />
          </div>
          <Input placeholder="Email (opcional)" type="email" value={form.email} onChange={set("email")} className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 rounded-sm h-11" data-testid="contact-email-input" />
          <select value={form.service_type} onChange={set("service_type")} className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-sm px-3 text-sm text-white" data-testid="contact-service-select">
            {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <Textarea placeholder="Contanos tu proyecto: medidas, materiales, ubicación de la obra... *" rows={4} value={form.message} onChange={set("message")} className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 rounded-sm" data-testid="contact-message-input" />
          <Button type="submit" disabled={sending} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase tracking-wider h-12" data-testid="contact-submit-btn">
            {sending ? "Enviando..." : "Enviar consulta"}
          </Button>
        </form>
      </section>

      {/* Footer */}
      <footer className="bg-[#141414] text-neutral-400 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="GR" className="h-12 w-auto rounded-sm bg-white p-1" />
            <div>
              <p className="font-heading font-bold uppercase text-white text-sm">GR Soluciones Integrales</p>
              <p className="text-xs">Estructuras · Portones · Rejas · Techos · Grandes obras</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span>© 2026 GR Soluciones Integrales</span>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-login-link">Acceso al panel</Link>
          </div>
        </div>
      </footer>

      {/* WhatsApp float */}
      <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_0_15px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform" data-testid="whatsapp-float-btn" title="Escribinos por WhatsApp" aria-label="Escribinos por WhatsApp">
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
